import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IPet from '../../interfaces/IPet'
import { SearchRequest, PetRestoreRequest } from '../../services/api/interfaces/ApiRequest'
import { petList, petRestore } from '../../services/api'

interface PetContextState extends BaseContextState {
  pets: Array<IPet> | null,
  pet: IPet | null
}

interface PetContextData extends PetContextState {
  resetErrors: Function,
  
  requestPetList: (args: SearchRequest) => void,
  requestPetFind: (args: SearchRequest) => void,
  requestPetRestore: (args: PetRestoreRequest) => void
}

// Create context
const { Provider, Consumer } = createContext({} as PetContextData)

// Export consumer
export const PetConsumer = Consumer

export class PetProvider extends BaseContextProvider {
  state: PetContextState = {
    pets: null,
    pet: null
  }

  /**
   * Make api request for pet list
   * 
   * @param {SearchRequest} payload 
   */
  requestPetList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      const response = await petList(payload)

      // Process our response
      this.processResponse(response, {
        paginate: {
          destination: 'pets'
        }
      })
    })
  }

  /**
   * Make api request for pet finder
   * 
   * @param {SearchRequest} payload 
   */
  requestPetFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      pet: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }

      const response = await petList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'pet'
        }
      })
    })
  }

  /**
   * Make api request for pet restore
   * 
   * @param {string} shelterUuid 
   * @param {PetRestoreRequest} payload 
   */
  requestPetRestore = (payload: PetRestoreRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      let response = await petRestore(payload)
      this.processResponse(response)

      // Refetch our data on success
      if (this.state.success) {
        response = await petList({
          search: {
            deleted: true,
            branch: {
              has: {
                shelter: {
                  has: {
                    uuid: payload.shelter_uuid
                  }
                }
              }
            }
          },
          options: {
            _force_ids: true
          }
        } as SearchRequest)

        // Process our response
        this.processResponse(response, {
          paginate: {
            destination: 'pets'
          }
        })
      }
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestPetList: this.requestPetList,
          requestPetFind: this.requestPetFind,
          requestPetRestore: this.requestPetRestore
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
